/** @jsxImportSource @emotion/react */
"use client";

import type { FunctionInterpolation, Interpolation } from "@emotion/react";
import { memo } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { numericPercentages, spacing } from "../../../../../global/emotion";
import { Text } from "../../../../components/_base/_core/Text/Text.emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import { useTranscriptDisplay } from "./TranscriptSettingsContext";
import { TranscriptThumbnail } from "./TranscriptThumbnail.emotion";

const headerStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.background,
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  padding: "76px 0 0",
  [cssMediaQueryMinWidthTablet]: { padding: "144px 0 0" },
  placeContent: "flex-start",
  placeItems: "center",
  pointerEvents: "none",
  position: "fixed",
  transition:
    "top 250ms ease-out, background-color 350ms ease-in-out, color 350ms ease-in-out",
  width: numericPercentages[100],
  willChange: "top, background-color, color",
});
const colorModeStyle: Interpolation<string> = (bgColor) => ({
  backgroundColor: bgColor,
});

const headerStageStyles: Record<string, Interpolation> = {
  expanded: {
    height: "229px",
    top: 0,
    [cssMediaQueryMinWidthTablet]: { height: "270px" },
  },
  collapsed: {
    top: "-94px",
    [cssMediaQueryMinWidthTablet]: { top: "-162px" },
  },
};

const defaultTitleStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
  marginBottom: spacing.m,
  marginTop: spacing.m,
  paddingInline: spacing.l,
  textAlign: "center",
  transition: "font-size 250ms ease-out, line-height 250ms ease-out",
});
const titleStyles: Record<
  "expanded" | "collapsed",
  FunctionInterpolation<number>
> = {
  expanded: (fontMultiplier) => ({
    fontSize: `${fontMultiplier * 20}px`,
    lineHeight: `${fontMultiplier * 24}px`,
  }),
  collapsed: (fontMultiplier) => ({
    fontSize: `${fontMultiplier * 12}px`,
    lineHeight: `${fontMultiplier * 14}px`,
  }),
};

interface TranscriptContentProps {
  title: string;
  imageUrl: string;
  isCurrentItem: boolean;
}

interface TranscriptHeaderProps {
  content: Pick<TranscriptContentProps, "title" | "imageUrl" | "isCurrentItem">;
  isHeaderCollapsed: boolean;
  playing: boolean;
  onPlay: () => void | Promise<void>;
}

export const TranscriptHeader = memo(
  ({ content, isHeaderCollapsed, playing, onPlay }: TranscriptHeaderProps) => {
    const { theme, colorHex, fontMultiplier } = useTranscriptDisplay();

    return (
      <header
        css={[
          headerStyle,
          theme === "color" ? colorModeStyle(colorHex) : null,
          headerStageStyles[isHeaderCollapsed ? "collapsed" : "expanded"],
        ]}
      >
        <TranscriptThumbnail
          activated={content.isCurrentItem}
          imageSrc={{ medium: content.imageUrl, color_hex: colorHex }}
          playing={playing}
          isHeaderCollapsed={isHeaderCollapsed}
          title={content.title}
          onPlay={onPlay}
        />
        <div>
          <Text
            as={"h1"}
            type={"title"}
            size={"m"}
            css={[
              defaultTitleStyle,
              titleStyles[isHeaderCollapsed ? "collapsed" : "expanded"](
                fontMultiplier,
              ),
            ]}
          >
            {content.title}
          </Text>
        </div>
      </header>
    );
  },
);
